import axios from "axios";

const apiUrl = process.env.REACT_APP_SOCKET_URL;

// ✅ Memory mein access token (global variable)
let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const clearAccessToken = () => {
  accessToken = null;
};

// =========================================
// Helper: logout + redirect
// =========================================
const forceLogout = () => {
  clearAccessToken();
  localStorage.removeItem("token");
  localStorage.removeItem("accessToken");
  // window.location.href = "/";
};

// =========================================
// Main axios instance
// =========================================
const axiosInstance = axios.create({
  baseURL: apiUrl + "api/v1/",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

// =========================================
// ✅ Separate instance SIRF refresh ke liye
// Ye response interceptor se nahi guzrega
// Isliye infinite loop ya transform issue nahi hoga
// =========================================
const refreshInstance = axios.create({
  baseURL: apiUrl + "api/v1/",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

// =========================================
// REQUEST INTERCEPTOR
// =========================================
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    const guestId = localStorage.getItem("x-guest-session-id");
    const selectedStoreId = process.env.REACT_APP_STOREID;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (guestId) {
      config.headers["x-guest-session-id"] = guestId;
    }

    if (selectedStoreId) {
      config.headers["X-Store-ID"] = selectedStoreId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// =========================================
// RESPONSE INTERCEPTOR
// =========================================
let isRefreshing = false;
let failedQueue = [];

// Nitya Seva member portal ka apna alag refresh lock hai — generic
// refresh (users/auth/refresh-token) se independent, taaki dono
// flows ek dusre ko block na karein
let isNsMemberRefreshing = false;
let nsMemberFailedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const processNsMemberQueue = (error, token = null) => {
  nsMemberFailedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  nsMemberFailedQueue = [];
};

axiosInstance.interceptors.response.use(
  // ✅ Success response transform
  (response) => {

    const { code, message, data, success } = response.data || {};
    return {
      code: code || (success ? 200 : 500),
      success: success !== undefined ? success : true,
      message: message || "Something went wrong! Try Again",
      data: data || null,
    };
  },

  // ✅ Error handler
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || "";

    // ── Nitya Seva ENROLLMENT flow (register/verify-otp/enroll) ──────────
    // Ye token kabhi refresh nahi hota — expire hone pe user ko dobara
    // OTP verify karni hi padti hai (backend ka jaanbujh kar liya gaya design)
    const isNityaSevaEnrollRoute =
      requestUrl.includes("nityaseva") && !requestUrl.includes("nityaseva/member");
    if (status === 401 && isNityaSevaEnrollRoute) {
      return Promise.reject(error);
    }

    // ── Nitya Seva MEMBER portal (login/profile) ──────────────────────────
    // Cookie-based refresh: nityaseva/member/refresh koi body nahi maangta,
    // httpOnly "nsMemberRefreshToken" cookie khud-b-khud bhej di jaati hai
    // (withCredentials: true ki wajah se), aur naya accessToken + naya
    // cookie milta hai.
    const isNityaSevaMemberRoute = requestUrl.includes("nityaseva/member");
    const isNsRefreshCallItself = requestUrl.includes("nityaseva/member/refresh");
    const isNsAuthEntryPoint =
      requestUrl.includes("nityaseva/member/login") || // also covers login-password
      requestUrl.includes("nityaseva/member/verify-otp") ||
      requestUrl.includes("nityaseva/member/resend-otp") ||
      requestUrl.includes("nityaseva/member/set-password");

    // A 401 from a login-style endpoint means "wrong credentials", not "expired
    // session" — there's nothing to refresh. Reject as-is so the caller can show
    // the server's real message (e.g. "Invalid email or password.") instead of
    // falling into the generic admin-style refresh below (users/auth/refresh-token
    // doesn't exist on this backend → confusing 404 + a forced logout).
    if (status === 401 && isNsAuthEntryPoint) {
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      isNityaSevaMemberRoute &&
      !isNsRefreshCallItself &&
      !isNsAuthEntryPoint
    ) {
      if (originalRequest._nsRetry) {
        // Refresh already try ho chuka, phir bhi 401 → session khatam,
        // local token clear kar do (cookie backend khud invalidate karega)
        clearAccessToken();
        localStorage.removeItem("accessToken");
        return Promise.reject(error);
      }

      if (isNsMemberRefreshing) {
        return new Promise((resolve, reject) => {
          nsMemberFailedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._nsRetry = true;
      isNsMemberRefreshing = true;

      try {
        // Body ki zarurat nahi — cookie hi refresh token carry karti hai
        const res = await refreshInstance.post(
          "nityaseva/member/refresh",
          {},
          { withCredentials: true },
        );

        const newAccessToken =
          res?.data?.data?.tokens?.accessToken || // same shape jaisa verify-otp deta hai
          res?.data?.data?.accessToken ||
          res?.data?.accessToken ||
          null;

        if (!newAccessToken) {
          console.error("Nitya Seva refresh response structure:", res?.data);
          throw new Error("accessToken not found in nityaseva/member/refresh response");
        }

        localStorage.setItem("accessToken", newAccessToken);
        setAccessToken(newAccessToken);
        processNsMemberQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error(
          "❌ Nitya Seva member token refresh failed:",
          refreshError?.response?.data || refreshError.message,
        );
        processNsMemberQueue(refreshError, null);
        clearAccessToken();
        localStorage.removeItem("accessToken");
        return Promise.reject(refreshError);
      } finally {
        isNsMemberRefreshing = false;
      }
    }

    // 401 handle karo (baaki saari routes — generic users/auth flow)
    if (status === 401) {
      // Agar ye request already retry ho chuki hai → logout
      if (originalRequest._retry) {
        forceLogout();
        return Promise.reject(error);
      }

      // Agar refresh already chal raha hai → queue mein daal do
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // ✅ Pehli baar 401 aayi — refresh try karo
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // ✅ refreshInstance use karo — ye axiosInstance ke interceptor se nahi guzrega
        // Isliye response { code, message, data } mein transform nahi hoga
        // Seedha axios raw response milega: res.data.data.accessToken ya res.data.accessToken
        const config = {
          withCredentials: true,
          headers: {},
        };

        const selectedStoreId = process.env.REACT_APP_STOREID;
        if (selectedStoreId) {
          config.headers["X-Store-ID"] = selectedStoreId;
        }

        const token = localStorage.getItem("accessToken");

        const res = await refreshInstance.post(
          "users/auth/refresh-token",
          { accessToken: token },
          config,
        );

        const newAccessToken =
          res?.data?.data?.accessToken || // backend ApiResponse: data.accessToken
          res?.data?.accessToken || // direct data
          res?.data?.data?.access_token || // legacy snake_case fallback
          res?.data?.access_token ||
          null;

        if (!newAccessToken) {
          console.error("Refresh response structure:", res?.data);
          throw new Error("accessToken not found in refresh response");
        }

        localStorage.setItem("accessToken", newAccessToken);
        setAccessToken(newAccessToken);
        processQueue(null, newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error(
          "❌ Token refresh failed:",
          refreshError?.response?.data || refreshError.message,
        );
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance; 